import { SxProps, Theme } from "@mui/material";
import RecipeHeader from "./RecipeHeader";
import RecipeImageAndDetails from "./RecipeImageAndDetails";
import RecipeDescription from "./RecipeDescription";

export const createRecipeStyles = (theme: Theme) => ({
  // Recipe Page Styles
  page: {
    container: {
      py: 4,
    } as SxProps<Theme>,

    mainCard: {
      borderRadius: 1,
      overflow: "hidden",
    } as SxProps<Theme>,

    mainHeader: {
      display: "flex",
      flexDirection: { xs: "column", md: "row" },
      alignItems: "center",
      justifyContent: "space-between",
      p: 3,
    } as SxProps<Theme>,

    headerInfo: {
      flexGrow: 1,
      mb: { xs: 2, md: 0 },
    } as SxProps<Theme>,

    refText: {
      color: "text.secondary",
      mb: 1,
    } as SxProps<Theme>,

    title: {
      color: theme.palette.materiom.seaweed,
      fontWeight: "bold",
    } as SxProps<Theme>,

    headerActions: {
      display: "flex",
      alignItems: "center",
      gap: 2,
    } as SxProps<Theme>,

    editButton: {
      bgcolor: "grey.200",
      color: "text.primary",
      textTransform: "none",
      "&:hover": {
        bgcolor: "grey.300",
      },
    } as SxProps<Theme>,

    actionButtons: {
      display: "flex",
      gap: 2,
      flexWrap: "wrap",
      justifyContent: { xs: "center", md: "flex-start" },
    } as SxProps<Theme>,

    forkButton: {
      textTransform: "none",
    } as SxProps<Theme>,

    contributeButton: {
      textTransform: "none",
    } as SxProps<Theme>,

    recipeMainSection: {
      display: "flex",
      flexDirection: { xs: "column", md: "row" },
      minHeight: 400,
    } as SxProps<Theme>,

    coverImageContainer: {
      width: { xs: "100%", md: "50%" },
      minHeight: { xs: 180, md: 220 },
      position: "relative",
      overflow: "hidden",
      bgcolor: "grey.100", // add this line
    } as SxProps<Theme>,

    coverImage: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      maxHeight: 310, // add maxHeight to further constrain
    } as SxProps<Theme>,

    detailsContainer: {
      p: 2, // reduced padding from 3 to 2
      width: "100%",
      height: "100%",
      minHeight: 180, // add minHeight to match image
    } as SxProps<Theme>,

    detailsGroup: {
      mt: 3,
    } as SxProps<Theme>,

    detailTitle: {
      fontWeight: "bold",
      color: "text.primary",
      mb: 1,
      fontSize: "1rem",
    } as SxProps<Theme>,

    detailValue: {
      color: "text.secondary",
    } as SxProps<Theme>,

    link: {
      color: "primary.main",
      textDecoration: "underline",
      "&:hover": {
        color: "primary.dark",
      },
    } as SxProps<Theme>,

    iconTextRow: {
      display: "flex",
      alignItems: "center",
      gap: 1,
    } as SxProps<Theme>,

    icon: {
      color: "primary.main",
    } as SxProps<Theme>,

    difficultyContainer: {
      display: "flex",
      gap: 1,
      mt: 1,
    } as SxProps<Theme>,

    difficultyDot: {
      width: 36, // was 20
      height: 36, // was 20
      borderRadius: "50%",
      transition: "background 0.2s",
    } as SxProps<Theme>,

    chipContainer: {
      display: "flex",
      flexWrap: "wrap",
      gap: 1,
      mt: 1,
    } as SxProps<Theme>,

    chip: {
      bgcolor: "grey.100",
    } as SxProps<Theme>,

    descriptionSection: {
      px: 3,
      borderRadius: 2,
      display: "flex",
      flexDirection: { xs: "column", md: "row" },
      gap: { xs: 3, md: 6 },
    } as SxProps<Theme>,

    descriptionDetails: {
      flex: "1 1 50%",
    } as SxProps<Theme>,

    toolsAndProcess: {
      flex: "1 1 50%",
      display: "flex",
      flexDirection: "column",
      gap: 3,
    } as SxProps<Theme>,

    sectionText: {
      mb: 2,
    } as SxProps<Theme>,

    sectionCard: {
      minHeight: 380,
      p: { xs: 2, sm: 3 },
    } as SxProps<Theme>,

    sectionHeader: {
      display: "flex",
      alignItems: "center",
      mb: 2,
    } as SxProps<Theme>,

    sectionTitle: {
      ml: 2,
      color: (theme: Theme) => theme.palette.materiom.seaweed,
      fontWeight: "bold",
    } as SxProps<Theme>,

    methodMainContainer: {
      display: "flex",
      flexDirection: "column",
      gap: 3,
    } as SxProps<Theme>,

    ambientConditionsSection: {
      display: "flex",
      flexDirection: "column",
      gap: 2,
    } as SxProps<Theme>,

    ambientTitle: {
      fontWeight: "bold",
      color: (theme: Theme) => theme.palette.materiom.seaweed,
    } as SxProps<Theme>,

    ambientRow: {
      display: "flex",
      mb: 1,
    } as SxProps<Theme>,

    ambientLabel: {
      fontWeight: "bold",
      width: "250px",
    } as SxProps<Theme>,

    methodStepsSection: {
      maxHeight: 500,
      overflowY: "auto",
      pr: 2,
    } as SxProps<Theme>,

    methodStepBox: {
      mb: 3,
    } as SxProps<Theme>,

    methodCard: {
      // Add any card-specific styles here
    } as SxProps<Theme>,

    methodStepHeader: {
      display: "flex",
      alignItems: "flex-start",
    } as SxProps<Theme>,

    stepNumber: {
      fontWeight: "bold",
      fontSize: 20,
      color: (theme: Theme) => theme.palette.materiom.seaweed,
      minWidth: 32,
      textAlign: "center",
    } as SxProps<Theme>,

    methodTypeRow: {
      pb: 1,
      borderBottom: "1px solid",
      borderColor: "grey.300",
      mb: 1,
    } as SxProps<Theme>,

    methodTitle: {
      color: "text.primary",
    } as SxProps<Theme>,

    methodTypeValue: {
      textTransform: "capitalize",
      fontWeight: "normal",
    } as SxProps<Theme>,

    methodDescriptionRow: {
      pb: 1,
      borderBottom: "1px solid",
      borderColor: "grey.300",
      mb: 1,
    } as SxProps<Theme>,

    methodDescriptionLabel: {
      fontWeight: "bold",
    } as SxProps<Theme>,

    methodDetailRow: {
      borderBottom: "1px solid",
      borderColor: "grey.300",
      pb: 1,
      mb: 1,
    } as SxProps<Theme>,

    methodDetailLabel: {
      fontWeight: "bold",
    } as SxProps<Theme>,

    methodDetailValue: {
      fontWeight: "normal",
    } as SxProps<Theme>,

    galleryImageContainer: {
      cursor: "pointer",
      boxShadow: 0,
      borderRadius: 2,
      overflow: "hidden",
    } as SxProps<Theme>,

    galleryImage: {
      width: "100%",
      height: 200,
      objectFit: "cover",
    } as SxProps<Theme>,

    lightboxCloseButton: {
      position: "absolute",
      top: 8,
      right: 8,
      zIndex: 1,
    } as SxProps<Theme>,

    lightboxCloseIcon: {
      fontSize: 40,
      color: "white",
    } as SxProps<Theme>,

    lightboxImage: {
      width: "100%",
      height: "100%",
      objectFit: "contain",
      p: 2,
    } as SxProps<Theme>,

    ingredientImage: {
      height: 120,
      width: "100%",
      objectFit: "cover",
    } as SxProps<Theme>,

    dialogTitle: {
      bgcolor: "primary.main",
      color: "white",
    } as SxProps<Theme>,

    dialogContent: {
      py: 3,
    } as SxProps<Theme>,

    dialogButton: {
      bgcolor: "primary.main",
      color: "white",
      px: 4,
      py: 1,
      borderRadius: 1,
      textDecoration: "none",
      "&:hover": {
        bgcolor: "primary.dark",
      },
    } as SxProps<Theme>,

    dialogCancelButton: {
      color: "text.primary",
    } as SxProps<Theme>,

    imageAndDetailsPaper: {
      mt: 0,
      borderRadius: 0,
      overflow: "visible",
    } as SxProps<Theme>,

    imageAndDetailsGrid: {
      display: "grid",
      gridTemplateColumns: {
        xs: "1fr",
        md: "1fr 1fr",
        lg: "3fr 2fr",
        xl: "5fr 3fr",
      },
      alignItems: "stretch",
      width: "100%",
      minHeight: {
        xs: "auto",
        md: 260,
        lg: 340,
        xl: 380,
      },
      gap: 0,
    } as SxProps<Theme>,

    imageColumn: {
      position: "relative",
      minWidth: 0,
      minHeight: {
        xs: 180,
        md: 260,
        lg: 340,
        xl: 380,
      },
      height: {
        xs: 180,
        md: 260,
        lg: 340,
        xl: 380,
      },
      bgcolor: "#DCDCDC",
      overflow: "hidden",
      width: "100%",
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
    } as SxProps<Theme>,

    detailsColumn: {
      width: "100%",
      bgcolor: "grey.100",
      minHeight: { xs: "auto", md: 260 },
      height: { xs: "auto", md: "100%" },
      boxSizing: "border-box",
      p: { xs: 2, sm: 3, md: 2, lg: 2.5, xl: 3 },
      overflowY: "visible",
    } as SxProps<Theme>,

    propertiesList: {
      mt: 2,
    } as SxProps<Theme>,

    propertyRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      borderBottom: "1px solid",
      borderColor: "grey.300",
      pb: 2,
      mb: 2,
    } as SxProps<Theme>,

    propertyLabel: {
      fontWeight: 600,
    } as SxProps<Theme>,

    propertyValue: {
      color: "text.secondary",
    } as SxProps<Theme>,
  },

  // Recipe Cards Styles
  cards: {
    cardContainer: {
      display: "flex",
      flexWrap: "wrap",
      gap: 3,
      justifyContent: "center",
    } as SxProps<Theme>,

    noRecipesContainer: {
      textAlign: "center",
      py: 4,
    } as SxProps<Theme>,

    cardWrapper: {
      flex: "1 1 250px",
      maxWidth: { xs: "100%", sm: "50%", md: "33.333%", lg: "25%" },
    } as SxProps<Theme>,

    cardLink: {
      textDecoration: "none",
      "&:hover": {
        textDecoration: "none",
      },
    } as SxProps<Theme>,

    card: {
      borderRadius: 2,
      overflow: "hidden",
      transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
      "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: `0 8px 16px rgba(0, 0, 0, 0.1)`,
      },
    } as SxProps<Theme>,

    imageWrapper: {
      position: "relative",
      width: "100%",
      height: 180,
    } as SxProps<Theme>,

    cardMedia: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
    } as SxProps<Theme>,

    cardContent: {
      p: 2,
      "&:last-child": {
        pb: 2,
      },
    } as SxProps<Theme>,

    cardTitle: {
      fontWeight: 600,
      fontSize: "1.1rem",
      color: "text.primary",
    } as SxProps<Theme>,
  },
});
